/**
 * LiveTestingCollapsibleField
 *
 * collapsible 타입 질문을 위한 필드 컴포넌트
 * V1 QsCollapsible/QsCollapsibleGroup/QsCollapsibleGroupItem의 V2 버전
 *
 * 기능:
 * - 접이식 그룹으로 증상 아이템 표시
 * - 그룹 열기/닫기
 * - 아이템 클릭 시 해당 트리의 질문들을 모달에서 표시
 * - 특수 옵션 (NoneAbove, Special) 처리
 */

import { memo, useMemo } from 'react';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import { V2QsCollapsible } from '../../../../shared/components/question-types/V2QsCollapsible';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import { useLiveTestingCollapsible, CollapsibleValue } from './useLiveTestingCollapsible';
import LiveTestingSxSelectModal from './LiveTestingSxSelectModal';
import LiveTestingContainer from './LiveTestingContainer';
import { getLocaleCode } from '../../../organization/configurations/language/constants';

interface LiveTestingCollapsibleFieldProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 값 */
  value: CollapsibleValue;
  /** 값 변경 핸들러 */
  onChange: (value: CollapsibleValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

function LiveTestingCollapsibleField({
  question,
  value,
  onChange,
  disabled = false,
}: LiveTestingCollapsibleFieldProps) {
  const { answerKeys, answerOptionsMap } = question;

  // Container에서 surveyId, locale 가져오기
  const { surveyId, currentLocale } = LiveTestingContainer.useContainer();

  const localeCode = getLocaleCode(currentLocale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });

  // collapsible hook 사용
  const {
    groups,
    openGroupKey,
    modalState,
    handleGroupToggle,
    handleItemClick,
    handleItemCheck,
    handleSpecialOptionCheck,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
  } = useLiveTestingCollapsible({
    answerKeys: answerKeys || [],
    answerOptionsMap: answerOptionsMap || null,
    value: value || { selectedKeys: [], itemAnswers: {} },
    onChange,
    locale: currentLocale,
    surveyId: surveyId ? Number(surveyId) : undefined,
  });

  // 현재 선택된 아이템 라벨
  const selectedItemLabel = useMemo(() => {
    if (!modalState.selectedItemKey) return '';

    // groups에서 아이템 찾기 (items 배열 내에서)
    const foundInItems = groups
      .flatMap((group) => group.items || [])
      .find((item) => item.key === modalState.selectedItemKey);
    if (foundInItems) return foundInItems.label;

    // 단일 아이템 그룹인 경우 (group.key가 직접 매칭)
    const foundGroup = groups.find((group) => group.key === modalState.selectedItemKey);
    if (foundGroup) return foundGroup.label;

    return modalState.selectedItemKey;
  }, [modalState.selectedItemKey, groups]);

  return (
    <div className="flex flex-col">
      {/* Collapsible UI */}
      <V2QsCollapsible
        groups={groups}
        openGroupKey={openGroupKey}
        onGroupToggle={handleGroupToggle}
        onItemClick={handleItemClick}
        onItemCheck={handleItemCheck}
        onSpecialOptionCheck={handleSpecialOptionCheck}
        disabled={disabled}
        loadingText={sltStr({ key: 'v2-qs-conditions-loading' })}
        addButtonText={sltStr({ key: 'btn-add', capit: true })}
        editButtonText={sltStr({ key: 'btn-edit', capit: true })}
      />

      {/* 증상 상세 모달 */}
      <LiveTestingSxSelectModal
        isOpen={modalState.isOpen}
        symptomName={selectedItemLabel}
        questions={modalState.questions}
        answers={modalState.answers}
        onAnswerChange={updateModalAnswer}
        onSave={handleModalSave}
        onClose={handleModalClose}
        isLoading={modalState.isLoading}
        error={modalState.error}
        saveButtonText={sltStr({ key: 'btn-save', capit: true })}
      />
    </div>
  );
}

export default memo(LiveTestingCollapsibleField);
